import { inject, Injectable, signal } from "@angular/core";
import { ModelStoreAbstraction } from "@bitwarden/web-vault/app/services/store/model.store.abstraction";
import { StoreModelNames } from "@bitwarden/web-vault/app/services/dali/type/dali.type";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { AccountsStoreCollection } from "@bitwarden/web-vault/app/services/store/user/accounts.store.collection";
import { VaultFilesStoreCollection } from "@bitwarden/web-vault/app/services/store/user/vault-files.store.collection";
import { WizardStoreCollection } from "@bitwarden/web-vault/app/services/store/user/wizards.store.collection";
import { CategoriesStoreCollection } from "@bitwarden/web-vault/app/services/store/user/categories.store.collection";
import { ClassificationsStoreCollection } from "@bitwarden/web-vault/app/services/store/user/classifications.store.collection";
import { PreferencesStoreCollection } from "@bitwarden/web-vault/app/services/store/user/preferences.store.collection";
import { InstitutionsStoreCollection } from "@bitwarden/web-vault/app/services/store/user/institutions.store.collection";
import { ConnectorsStoreCollection } from "@bitwarden/web-vault/app/services/store/user/connectors.store.collection";
import {
  ModelCollectionInterface,
  StoreModelCollection,
  UserStoreModelCollectionType,
} from "@bitwarden/web-vault/app/services/store/model.collection.abstraction";
import { ExternalSubscriptionStoreCollection } from "@bitwarden/web-vault/app/services/store/user/external.subscription.store.collection";
import { ExternalUserCollection } from "@bitwarden/web-vault/app/services/store/user/external.user.collection";
import { KeyStoreCollection } from "@bitwarden/web-vault/app/services/store/user/key.store.collection";
import { PluginStoreCollection } from "@bitwarden/web-vault/app/services/store/user/plugin.store.collection";
import { distinctUntilChanged, filter, Observable, skipUntil, Subscription } from "rxjs";
import { toObservable } from "@angular/core/rxjs-interop";
import { InstitutionView } from "@bitwarden/web-vault/app/models/view/institution/institution.view";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import { EnrichAccountGeneratedCollection } from "@bitwarden/web-vault/app/services/store/user/enrich-account.generated.collection";
import { SearchData } from "@bitwarden/web-vault/app/services/store/search.abstraction";
import { Origin } from "@bitwarden/web-vault/app/models/types/general-types";
import { GLOSS_CREDITS_ACCOUNT_NAME } from "@bitwarden/web-vault/app/models/constants/global.constants";
import { GLOSS_VAULT_ACCOUNT_ID_SUFFIX } from "@bitwarden/web-vault/app/shared/utils/helper.institution/gloss-vault-institution.helper";

@Injectable({
  providedIn: "root",
})
export class UserStoreService
  extends ModelStoreAbstraction
  implements ModelCollectionInterface<UserStoreModelCollectionType>
{
  readonly preferences: PreferencesStoreCollection = new PreferencesStoreCollection();
  readonly wizards: WizardStoreCollection = new WizardStoreCollection();
  readonly accounts: AccountsStoreCollection = new AccountsStoreCollection();
  readonly vaultFiles: VaultFilesStoreCollection = new VaultFilesStoreCollection();
  readonly categories: CategoriesStoreCollection = new CategoriesStoreCollection();
  readonly classifications: ClassificationsStoreCollection = new ClassificationsStoreCollection();
  readonly institutions: InstitutionsStoreCollection = new InstitutionsStoreCollection();
  readonly connectors: ConnectorsStoreCollection = new ConnectorsStoreCollection();
  readonly enrichAccounts: EnrichAccountGeneratedCollection =
    new EnrichAccountGeneratedCollection();
  readonly subscription: ExternalSubscriptionStoreCollection =
    new ExternalSubscriptionStoreCollection();
  readonly user = new ExternalUserCollection();
  readonly key = new KeyStoreCollection();
  readonly plugin = new PluginStoreCollection();

  protected log: LogService = inject(LogService);
  protected institutions$: Observable<InstitutionView[]> = toObservable(
    this.institutions.institutionViews,
  );
  protected accounts$: Observable<AccountView[]> = toObservable(this.accounts.accountViews);
  showBalance = signal(sessionStorage.getItem("showBalance") !== "false");

  private subscriptions: Subscription[] = [];

  clearStore(): void {
    this.subscriptions.forEach((s) => {
      s.unsubscribe();
    });

    this.preferences.clear();
    // this.wizards.clear();
    this.accounts.clear();
    this.vaultFiles.clear();
    this.categories.clear();
    this.classifications.clear();
    this.institutions.clear();
    this.connectors.clear();
    this.subscription.clear();
    this.enrichAccounts.clear();
    this.user.clear();
    this.key.clear();
    this.plugin.clear();
    this.log.info("UserStoreService cleared");
  }

  /**
   * Heuristic: treated as first-ever login when there is no GLOSS Credits account in the vault yet.
   *
   * TODO: Replace this with a definitive first-ever-login signal (e.g. API/user profile flag,
   * created-at vs first-sync, or a dedicated vault preference)—GLOSS Credits may be created after
   * login and is not a reliable long-term definition.
   */
  isFirstEverLogin(): boolean {
    const accounts = this.accounts.accountViews();
    if (!accounts?.length) {
      return true;
    }
    const hasGlossCreditsAccount = accounts.some((a) => {
      const instAccId = a?.institutionLink?.institutionAccountId;
      return (
        (instAccId && instAccId.endsWith(GLOSS_VAULT_ACCOUNT_ID_SUFFIX)) ||
        a.name === GLOSS_CREDITS_ACCOUNT_NAME
      );
    });
    return !hasGlossCreditsAccount;
  }

  async initialize(): Promise<void> {
    this.log.info("Initializing UserStoreService");
    this.preferences.triggerChanges();
    this.wizards.triggerChanges();
    this.institutions.triggerChanges();
    this.accounts.triggerChanges();
    this.subscription.triggerChanges();
    // Not awaited (#6401): Tier 1 (and its overlay) mustn't wait on GET /users/:id, and nothing here
    // reads it. Until it loads, its readers show what they show while it's unknown: Settings keeps
    // its Billing link and Storage the default usage. It never rejects, so this can't be an
    // unhandled rejection.
    void this.user.triggerChanges();
    await this.categories.initialize();
    await this.classifications.initialize();
    this.categories.triggerChanges();
    this.classifications.triggerChanges();
    this.connectors.triggerChanges();
    this.vaultFiles.triggerChanges();
    this.key.triggerChanges();
    this.plugin.triggerChanges();
    this.initialiseCollectionListener();
  }

  private initialiseCollectionListener(): void {
    this.subscribeToDataChange();
  }

  /**
   * Subscription for external data change in other stores
   **/
  private subscribeToDataChange() {
    /** Insitution Data must be loaded before we can enrich accounts **/
    const institutionChange$ = this.institutions$.pipe(
      filter((x) => !!x),
      distinctUntilChanged(
        (previous, current) =>
          previous === current || (previous.length === 0 && current.length === 0),
      ),
    );

    const accountsChange$ = this.accounts$.pipe(
      filter((x) => !!x),
      distinctUntilChanged((previous, current) => {
        return previous === current || (previous.length === 0 && current.length === 0);
      }),
      skipUntil(institutionChange$),
    );

    const accountSubscription = accountsChange$.subscribe(this.onAccountsUpdated.bind(this));
    this.subscriptions.push(accountSubscription);
  }

  protected getStorageCollection(modelName: StoreModelNames): StoreModelCollection {
    switch (modelName) {
      case "AccountStoreModel":
        return this.accounts;
      case "CategoryStoreModel":
        return this.categories;
      case "ClassificationStoreModel":
        return this.classifications;
      case "ConnectorStoreModel":
        return this.connectors;
      case "InstitutionStoreModel":
        return this.institutions;
      case "PreferenceStoreModel":
        return this.preferences;
      // case "SubscriptionStoreModel":
      //   return this.subscription;
      case "VaultFileStoreModel":
        return this.vaultFiles;
      case "WizardStoreModel":
        return this.wizards;
      case "KeyStoreModel":
        return this.key;
      case "PluginStoreModel":
        return this.plugin;
      default: {
        throw new Error(`UserStoreService doesnt implement ${modelName}`);
      }
    }
  }

  search(input: string): SearchData {
    const searchTerm = input.trim().toLowerCase();

    return {
      account: (this.enrichAccounts.collection() || []).filter((acc) =>
        acc.name?.trim().toLowerCase().includes(searchTerm),
      ),
      cabinet: (this.vaultFiles.vaultFileViews() || []).filter((file) =>
        file.name?.trim().toLowerCase().includes(searchTerm),
      ),
    };
  }

  onAccountsUpdated(accounts: AccountView[]) {
    const institutions = this.institutions.institutionViews();
    this.enrichAccounts.generate(institutions, accounts);
  }

  setShowBalance(value: boolean) {
    sessionStorage.setItem("showBalance", value.toString());
    this.showBalance.set(value);
  }

  /** Gets all the accounts that are synced from Basiq */
  getBasiqAccounts(): AccountView[] {
    return this.accounts.accountViews().filter((account) => account.origin === Origin.basiq);
  }

  /** Gets all the accounts that are synced from Fiskil */
  getFiskilAccounts(): AccountView[] {
    return this.accounts.accountViews().filter((account) => account.origin === Origin.fiskil);
  }

  /** Gets all the accounts that are synced from Plaid */
  getPlaidAccounts(): AccountView[] {
    return this.accounts.accountViews().filter((account) => account.origin === Origin.plaid);
  }
}
